/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps, Fragment, Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { MutableRef } from 'preact/hooks';
import { FocusableHandle, useFocusableTextField } from '../hooks/UNSAFE_useFocusableTextField';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useLoadingIndicatorTimer } from '../hooks/UNSAFE_useLoadingIndicatorTimer';
import { useTextField } from '../hooks/UNSAFE_useTextField';
import { DataState } from '../UNSAFE_Collection';
import { Label } from '../UNSAFE_Label';
import { VirtualizedListView } from '../UNSAFE_VirtualizedListView';
import { LiveRegion } from '../UNSAFE_LiveRegion';
import {
  ReadonlyTextField,
  ReadonlyTextFieldInput,
  TextField,
  TextFieldInput
} from '../UNSAFE_TextField';
import {
  CompactUserAssistance,
  InlineUserAssistance,
  UserAssistanceDensityType
} from '../UNSAFE_UserAssistance';
import { Item } from '../utils/UNSAFE_dataProvider';
import { Size } from '../utils/UNSAFE_size';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';
import {
  DefaultList,
  Dropdown,
  DropdownArrow,
  DropdownList,
  DropdownUserAssistance,
  EmptyResults,
  isBeforeDataFetch,
  isMobile
} from '../PRIVATE_SelectCommon';
import { ItemTextType } from '../utils/UNSAFE_selectUtils';
import { useDisplayValue } from './useDisplayValue';
import { useSelectSingle } from './useSelectSingle';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';

type TextFieldInputProps = ComponentProps<typeof TextFieldInput>;
type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type Props<K extends string | number, D extends Record<string, any>> = {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: TextFieldInputProps['aria-describedby'];
  /**
   * Text to provide guidance to help the user understand what data to enter or select.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];
  /**
   * Specifies data for the dropdown list.
   *
   * Note that the <code>itemText</code> property allows for customizing the rendering
   * of each data item.
   */
  data?: DataState<K, D> | null;
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  /**
   * Specifies whether the component is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies whether the component is waiting for <code>valueItems</code> to load.
   * While loading, SelectSingle will show the progressive loading indicator in the text field.
   */
  isLoading?: boolean;
  /**
   * Specifies whether the component is readonly.
   */
  isReadonly?: boolean;
  /**
   * Specifies for accessibility purposes whether a value is required.
   *
   * Setting this property to <code>false</code> means that a value is not required to be
   * selected by the user.  Setting this property to <code>true</code> means that a value
   * is required to be selected by the user.
   */
  isRequired?: TextFieldInputProps['isRequired'];
  /**
   * Specifies whether to show an indicator on screen that a value is required, for example
   * before the user has selected a value.
   */
  isRequiredShown?: InlineUserAssistanceProps['isRequiredShown'];
  /**
   * Specifies how to get the text string to render for a data item.
   * This property can be set to either:
   * <ul>
   * <li>a string that specifies the name of a top level data attribute to render as text, or</li>
   * <li>a callback function that takes a properties object and returns the text string to
   * display.</li>
   * </ul>
   *
   * This text will be rendered for the selected <code>valueItems</code> of the component.
   * It will also be rendered for each item in the dropdown.  When rendered for the
   * dropdown items, default matching search term highlighting will still be applied.
   */
  itemText: ItemTextType<K, D>;
  /**
   * Specifies the label associated with the field.
   */
  label: string;
  /**
   * Specifies where the label is positioned relative to the field.
   */
  labelEdge?: 'inside' | 'start' | 'top' | 'none';
  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>
   */
  labelStartWidth?: Size;
  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];
  /**
   * A short hint to display before the user selects or enters a value.
   */
  placeholder?: TextFieldInputProps['placeholder'];
  /**
   * Specifies how to align text within the field.
   */
  textAlign?: TextFieldInputProps['textAlign'];
  /**
   * Specifies the density of the user assistance presentation.  It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline.  Layout will reflow when text is displayed.</li>
    * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
   
  * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;
  /**
   * Specifies the key, data, and optional metadata for the selected value.
   */
  valueItem?: Item<K, D>;
  /**
   * The style variant of the component.
   */
  variant?: TextFieldInputProps['variant'];
  /**
   * The type of virtual keyboard to display for entering a value on mobile browsers.
   * This property has no effect on desktop browsers.
   */
  virtualKeyboard?: 'auto' | 'email' | 'number' | 'search' | 'tel' | 'text' | 'url';
  /**
   * Callback invoked when the user commits the selected value.
   *
   * The function should set a new <code>valueItem</code> property.
   */
  onCommit: (detail: ValueUpdateDetail<K>) => void;
  /**
   * Callback function to trigger loading data for the dropdown list, which may or may not be
   * filtered by user entered search text.
   *
   * The function should set a new <code>DataState</code> as the <code>data</code> property.
   */
  onFilter?: ({ searchText }: { searchText?: string }) => void;
  /**
   * Callback function to handle when the viewport of the dropdown list has changed, for example
   * by scrolling, including the case where the user scrolls to the end of the list and there are
   * more items to load.
   *
   * The function should set a new <code>DataState</code> for the specified range as the
   * <code>data</code> property.
   */
  onLoadRange?: ComponentProps<typeof VirtualizedListView>['onLoadRange'];
};

/**
 * A SelectSingle is a dropdown list that supports single selection and search filtering.
 */
export const SelectSingle = forwardRef(
  <K extends string | number, D extends Record<string, any>>(
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      data,
      helpSourceLink,
      helpSourceText,
      isDisabled: propIsDisabled,
      isLoading: propIsLoading,
      isReadonly: propIsReadonly,
      isRequired,
      isRequiredShown,
      itemText,
      label,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      messages,
      onCommit,
      onFilter,
      onLoadRange,
      placeholder,
      textAlign: propTextAlign,
      userAssistanceDensity: propUserAssistanceDensity,
      valueItem,
      variant = 'default',
      virtualKeyboard
    }: Props<K, D>,
    ref?: Ref<FocusableHandle>
  ) => {
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      textAlign: formTextAlign,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const textAlign = propTextAlign ?? formTextAlign;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const {
      enabledElementRef,
      focusProps,
      isFocused: origIsFocused,
      readonlyElementRef
    } = useFocusableTextField<HTMLInputElement, HTMLDivElement>({ isDisabled, isReadonly, ref });

    const {
      collectionProps,
      dropdownArrowEventHandlers,
      dropdownEventHandlers,
      dropdownRef,
      inputEventHandlers,
      isDropdownAbove,
      isDropdownOpen,
      isFocused,
      isUserFiltering,
      mainFieldRef,
      mouseProps,
      searchText,
      userInput
    } = useSelectSingle<K, D>({
      data,
      inputRef: enabledElementRef as MutableRef<HTMLInputElement>,
      isDisabled,
      isFocused: origIsFocused,
      isReadonly,
      onCommit,
      onFilter,
      valueItem
    });

    // The incoming propIsLoading tells us that we are in a loading state, but we don't want to
    // show the loading indicator until after a delay, because showing it immediately could result
    // in unwanted flashing.  The isLoading var below will be set to true by useLoadingIndicatorTimer
    // after the delay, when the timer expires, at which point we should show the loading indicator.
    // If propIsLoading is set to false before the timer expires, then the timer will be cancelled
    // and no loading indicator will be shown.
    // (ListView detects its loading state by checking data === null.  While it would be nice for
    // us to be consistent and check valueItems === null, we need to use a separate isLoading prop.
    // If valueItems === null triggers our loading state, then the selections in the dropdown
    // are cleared for that period of time, which is undesirable.)
    const isLoading = useLoadingIndicatorTimer(propIsLoading ?? false);

    const {
      baseId,
      formFieldContext,
      inputProps,
      labelProps,
      textFieldProps,
      userAssistanceProps
    } = useTextField({
      ariaDescribedBy,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isFocused,
      isLoading,
      isReadonly,
      isRequiredShown,
      labelEdge,
      messages,
      styleVariant: variant,
      userAssistanceDensity,
      value: valueItem !== undefined ? true : undefined
    });

    // JET-54256 - inside label animates up on initial display with selected value
    // If we're waiting for the selected values to load, the component should look like it
    // has a value in the meantime.
    // Use the original propIsLoading instead of isLoading because we don't want to delay
    // the inside label position by a timer like we do the progressive loading indicator.
    formFieldContext.hasValue = propIsLoading || formFieldContext.hasValue;

    const dropdownId = `${baseId}-dropdown`;

    const displayValue = useDisplayValue(itemText, valueItem);

    const labelComp = labelEdge !== 'none' ? <Label {...labelProps}>{label}</Label> : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined
    };

    const ariaLabel = labelEdge === 'none' ? label : undefined;
    const isInlineDensity =
      userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow';

    const inlineUserAssistance = isInlineDensity ? (
      isDisabled || isReadonly ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled or readonly fields
        userAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            userAssistanceDensity={userAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          messages={messages}
          isRequiredShown={isRequiredShown}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      )
    ) : undefined;

    const compactUserAssistance =
      userAssistanceDensity === 'compact' ? (
        <CompactUserAssistance
          anchorRef={mainFieldRef}
          messages={messages}
          assistiveText={assistiveText}
          {...userAssistanceProps}
        />
      ) : undefined;

    const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

    if (isReadonly) {
      return (
        <FormFieldContext.Provider value={formFieldContext}>
          <ReadonlyTextField
            role="presentation"
            compactUserAssistance={compactUserAssistance}
            inlineUserAssistance={inlineUserAssistance}
            onBlur={focusProps.onfocusout}
            onFocus={focusProps.onfocusin}
            ref={mainFieldRef}
            {...fieldLabelProps}>
            <ReadonlyTextFieldInput
              aria-describedby={inputProps['aria-describedby']}
              aria-label={ariaLabel}
              aria-labelledby={labelProps.id}
              as="div"
              elementRef={readonlyElementRef}
              hasEmptyLabel={label === '' && labelEdge === 'none'}
              hasInsideLabel={label !== undefined && labelEdge === 'inside'}
              textAlign={textAlign}
              value={displayValue}
            />
          </ReadonlyTextField>
        </FormFieldContext.Provider>
      );
    }

    const isDataFetched = data !== undefined && data !== null && !isBeforeDataFetch<K, D>(data);
    const hasInsideLabel = labelComp !== undefined && labelEdge === 'inside';
    const liveRegionText =
      isDropdownOpen && isDataFetched
        ? data?.totalSize === 0
          ? translations.select_noMatchesFound()
          : data?.totalSize === 1
          ? translations.select_oneMatchFound()
          : data?.sizePrecision === 'exact'
          ? translations.select_sizeMatchesFound({ TOTAL_SIZE: `${data?.totalSize}` })
          : translations.select_sizeOrMoreMatchesFound({ TOTAL_SIZE: `${data?.totalSize}` })
        : '';

    const mainContent = (
      <Fragment>
        <TextFieldInput
          aria-autocomplete="list"
          aria-controls={dropdownId}
          aria-expanded={isDropdownOpen}
          aria-label={ariaLabel}
          autoComplete="off"
          hasEmptyLabel={label === '' && labelEdge === 'none'}
          hasInsideLabel={hasInsideLabel}
          inputRef={enabledElementRef}
          isRequired={isRequired}
          // onCommit={handleCommit}
          {...inputEventHandlers}
          placeholder={placeholder}
          role="combobox"
          spellcheck={false}
          textAlign={textAlign}
          type={isMobile() ? (virtualKeyboard === 'auto' ? 'search' : virtualKeyboard) : undefined}
          value={isUserFiltering ? userInput : displayValue}
          {...inputProps}
        />
        <LiveRegion>{liveRegionText}</LiveRegion>
      </Fragment>
    );

    const dropdownArrow = (
      <DropdownArrow
        isDisabled={isDisabled}
        size={hasInsideLabel ? 'md' : 'sm'}
        {...dropdownArrowEventHandlers}
      />
    );

    const dropdownInlineUserAssistance =
      isDropdownOpen &&
      !isDropdownAbove &&
      !isDisabled &&
      !isReadonly &&
      (assistiveText || helpSourceLink) &&
      (userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow') ? (
        <DropdownUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          userAssistanceDensity={userAssistanceDensity}
        />
      ) : undefined;

    const isEmptyResults = isDataFetched && data?.totalSize === 0;
    const dropdownListContent = isEmptyResults ? (
      <EmptyResults />
    ) : (
      <DefaultList<K, D>
        {...collectionProps}
        aria-label={label ?? ''}
        currentItemVariant="highlight"
        data={data}
        itemText={itemText}
        onLoadRange={onLoadRange}
        searchText={isUserFiltering ? searchText : undefined}
        selectionMode="single"
      />
    );

    return (
      <Fragment>
        <FormFieldContext.Provider value={formFieldContext}>
          <TextField
            compactUserAssistance={compactUserAssistance}
            endContent={isLoading ? undefined : dropdownArrow}
            inlineUserAssistance={inlineUserAssistance}
            mainContent={mainContent}
            mainFieldRef={mainFieldRef}
            onBlur={focusProps?.onfocusout}
            onFocus={focusProps?.onfocusin}
            {...textFieldProps}
            {...fieldLabelProps}
            {...mouseProps}
          />
        </FormFieldContext.Provider>
        <Dropdown
          dropdownRef={dropdownRef}
          id={dropdownId}
          isOpen={
            isDropdownOpen &&
            data !== undefined &&
            (data === null || data.totalSize > 0 || isBeforeDataFetch<K, D>(data) || isEmptyResults)
          }
          anchorRef={mainFieldRef}
          {...dropdownEventHandlers}>
          <DropdownList
            hasBottomGap={dropdownInlineUserAssistance === undefined}
            isLoading={data === null}>
            {dropdownListContent}
          </DropdownList>
          {dropdownInlineUserAssistance}
        </Dropdown>
      </Fragment>
    );
  }
);
